import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
import { trackAddToCart } from '../hooks/useMetaPixel';
const protocols = [{
  icon: '🏜️',
  title: 'Protocol 1 — "The Desert Brisket"',
  symptoms: 'No shine, crumbles, zero juice',
  solution: 'Golden Broth Emergency Injection + Steam Seal at 170°F',
  time: '⏱️ 15 minutes',
  borderColor: 'border-[#E69D4F]'
}, {
  icon: '🔩',
  title: 'Protocol 2 — "The Rubber Tire"',
  symptoms: "Probe won't enter, fibers resist like concrete",
  solution: 'Hermetic Oven Rescue at 275°F + Mandatory Cooler Rest',
  time: '⏱️ 60–90 minutes',
  borderColor: 'border-[#EF7D43]'
}, {
  icon: '💧',
  title: 'Protocol 3 — "The Mushy Mess"',
  symptoms: "Falls apart, zero structure, can't slice",
  solution: 'Championship Burnt Ends or Chopped Beef Pivot',
  time: '⏱️ 20 minutes',
  borderColor: 'border-[#F2B851]'
}];
const checklist = ['A 30-second rapid diagnosis system — identify exactly what went wrong before you panic, before you Google, before your guests notice', 'The "Golden Broth" Protocol — restaurant-grade rehydration using beef tallow and bone broth that rescues a dry brisket in exactly 15 minutes flat', 'The Oven Rescue Method — your smoker already did its job. Now science finishes it at 275°F with a hermetic seal while you buy yourself time', 'Championship Burnt Ends Pivot — how to turn any overcooked disaster into the dish your guests specifically request next time', 'The 4 panic errors that turn a fixable brisket into actual dog food — and the mindset shift that stops you from making them'];
export function BonusSection() {
  return <section className="py-16 md:py-20 px-4 bg-[#000000]" data-id="element-22">
      <div className="max-w-4xl mx-auto" data-id="element-23">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-24">
          <div className="inline-block bg-gradient-to-r from-[#F2B851] to-[#C45F2A] text-[#0F1111] px-6 py-1.5 rounded-full font-black text-xs md:text-sm uppercase tracking-widest mb-6" data-id="element-25">
            ⚡ LIMITED BONUS — Included FREE Today Only
          </div>
          <h2 className="font-heading text-5xl md:text-7xl font-black text-[#E69D4F] mb-2 uppercase tracking-tight" data-id="element-26">
            🆘 Brisket Rescue 911
          </h2>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-ivory mb-4 uppercase tracking-wide" data-id="element-27">
            Emergency Protocols for Pitmasters
          </h3>
          <p className="text-lg md:text-xl text-[#EF7D43] italic max-w-3xl mx-auto" data-id="element-28">
            How to save a $100 cut from the trash in 20 minutes or less —
            because failure is not an option when guests are already waiting
          </p>
        </motion.div>

        {/* Product Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="w-full max-w-2xl mx-auto rounded-2xl overflow-hidden mb-12" data-id="element-29">
          <img src="https://cdn.magicpatterns.com/uploads/sfFySb2QLRp7FJJU4xwF6Q/BONO_Brisket_Rescue_911.png" alt="Brisket Rescue 911 — Emergency Protocols for Pitmasters" className="w-full h-auto object-cover" data-id="element-30" />
        </motion.div>

        {/* Emotional Hook */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="border-l-4 border-[#E69D4F] pl-5 md:pl-8 py-2 mb-16" data-id="element-31">
          <div className="space-y-5 text-smoke text-base md:text-lg leading-relaxed" data-id="element-32">
            <p className="text-ivory font-bold text-lg md:text-xl" data-id="element-33">
              It's 6:00 PM. Your guests are already in the backyard.
            </p>
            <p data-id="element-34">
              You open the lid and insert the probe. It feels like pushing a
              screwdriver through a car tire.
            </p>
            <p data-id="element-35">
              The brisket you've been smoking for 14 hours — the $100 full
              packer you drove 40 minutes to buy — is either bone dry, tough as
              rubber, or falling apart like wet newspaper.
            </p>
            <p data-id="element-36">
              The Heat-Flow System teaches you how to cook it perfectly. But
              this bonus exists for the day the unexpected happens.
            </p>
            <p data-id="element-37">
              Because even the best pitmasters in Texas have had a cook go
              sideways. The difference between a pro and an amateur isn't
              perfection. It's knowing exactly what to do in the next 20
              minutes.
            </p>
          </div>
        </motion.div>

        {/* Protocols */}
        <div className="mb-16" data-id="element-38">
          <h3 className="font-heading text-3xl md:text-4xl font-black text-ivory mb-8 uppercase tracking-tight text-center" data-id="element-39">
            The 3 Emergency Protocols
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-40">
            {protocols.map((protocol, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`bg-dark-elevated rounded-2xl p-6 border-t-4 ${protocol.borderColor}`} data-id="element-41">
                <div className="text-4xl mb-4" data-id="element-42">{protocol.icon}</div>
                <h4 className="font-heading text-xl md:text-2xl font-bold text-ivory mb-4 uppercase tracking-tight" data-id="element-43">
                  {protocol.title}
                </h4>
                <ul className="space-y-3 text-smoke text-sm md:text-base" data-id="element-44">
                  <li className="flex items-start gap-2" data-id="element-45">
                    <span className="text-red-500 font-bold shrink-0" data-id="element-46">→</span>
                    <span data-id="element-47">{protocol.symptoms}</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-48">
                    <span className="text-green-500 font-bold shrink-0" data-id="element-49">→</span>
                    <span className="text-ivory font-medium" data-id="element-50">
                      {protocol.solution}
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-51">
                    <span className="text-[#E69D4F] font-bold shrink-0" data-id="element-52">→</span>
                    <span data-id="element-53">{protocol.time}</span>
                  </li>
                </ul>
              </motion.div>)}
          </div>
        </div>

        {/* What's Inside */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-dark border border-[#333] rounded-2xl p-6 md:p-10 mb-16" data-id="element-54">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-ivory mb-6 md:mb-8 pb-4 border-b border-[#333] uppercase tracking-tight" data-id="element-55">
            What's Inside:
          </h3>
          <ul className="space-y-4 md:space-y-6" data-id="element-56">
            {checklist.map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-57">
                <CheckCircleIcon className="w-6 h-6 md:w-7 md:h-7 text-green-500 shrink-0 mt-0.5" data-id="element-58" />
                <span className="text-smoke leading-relaxed text-base md:text-lg" data-id="element-59">
                  {item}
                </span>
              </li>)}
          </ul>
        </motion.div>

        {/* Value Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="rounded-2xl overflow-hidden border border-[#3A2A1A] mb-12 bg-[#1C1410]" data-id="element-60">
          <div className="bg-gradient-to-r from-[#E69D4F] to-[#C45F2A] px-4 py-3 md:px-8 md:py-5 border-b-4 border-[#964215]" data-id="element-61">
            <h3 className="font-heading text-xl md:text-3xl font-black text-[#0F1111] uppercase tracking-tight" data-id="element-62">
              What You Get Today
            </h3>
          </div>

          <div className="flex flex-col" data-id="element-63">
            <div className="px-4 sm:px-8 py-5 flex items-center justify-between gap-4 border-b border-[#3A2A1A]/50" data-id="element-64">
              <div className="flex-1" data-id="element-65">
                <h4 className="text-ivory font-bold text-base md:text-lg leading-tight" data-id="element-66">
                  The Backyard Pitmaster
                </h4>
                <p className="text-[#8A7A6A] text-xs md:text-sm mt-1" data-id="element-67">
                  30-Day Heat-Flow Blueprint
                </p>
              </div>
              <div className="text-right shrink-0" data-id="element-68">
                <span className="text-[#8A7A6A] line-through text-sm mr-2" data-id="element-69">
                  $29
                </span>
                <span className="text-[#E69D4F] font-heading font-bold text-xl md:text-2xl" data-id="element-70">
                  $6.99
                </span>
              </div>
            </div>

            <div className="px-4 sm:px-8 py-5 flex items-center justify-between gap-4 bg-[#E69D4F]/5" data-id="element-71">
              <div className="flex-1" data-id="element-72">
                <h4 className="text-[#E69D4F] font-bold text-base md:text-lg leading-tight flex items-center gap-2" data-id="element-73">
                  <span data-id="element-74">🎁</span> Brisket Rescue 911
                </h4>
                <p className="text-[#8A7A6A] text-xs md:text-sm mt-1" data-id="element-75">
                  Full Emergency Guide (PDF)
                </p>
              </div>
              <div className="text-right shrink-0" data-id="element-76">
                <span className="text-[#8A7A6A] line-through text-sm mr-2" data-id="element-77">
                  $27
                </span>
                <span className="text-green-500 font-heading font-bold text-xl md:text-2xl" data-id="element-78">
                  FREE
                </span>
              </div>
            </div>
          </div>

          <div className="border-t-2 border-[#3A2A1A] px-4 sm:px-8 py-5 flex justify-between items-center bg-[#161010]" data-id="element-79">
            <p className="text-[#8A7A6A] text-sm md:text-base font-heading uppercase tracking-widest" data-id="element-80">
              You Save
            </p>
            <p className="text-green-500 font-heading font-black text-2xl md:text-3xl" data-id="element-81">
              $49.01
            </p>
          </div>
        </motion.div>

        {/* Emotional Close & CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center" data-id="element-82">
          <div className="space-y-4 text-smoke text-base md:text-lg leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-83">
            <p data-id="element-84">
              At $6.99, you're already getting the complete Heat-Flow System
              that 4,812 pitmasters used to transform their backyard cooks.
            </p>
            <p data-id="element-85">
              The Brisket Rescue 911 guide is your insurance policy. Your
              emergency kit. Your "break glass in case of dry brisket" manual.
            </p>
            <p className="text-ivory font-medium" data-id="element-86">
              Both are covered by the 14-Day "Probe-Tender" Guarantee. No
              questions. No hassle. Jake's word on it.
            </p>
          </div>

          <a href="https://www.vitalyforms.com/cart/50069069365468:1" target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="w-full sm:w-auto inline-block text-center bg-gradient-to-r from-[#F2B851] to-[#C45F2A] border-b-4 border-[#964215] rounded-md text-[#0F1111] font-black uppercase text-base sm:text-xl md:text-2xl py-4 md:py-6 px-6 md:px-16 shadow-[0_0_30px_rgba(242,184,81,0.5)] hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0.5 active:border-b-2 transition-all duration-200" data-id="element-87">
            🔥 YES — Get Both for Just $6.99 →
          </a>
          <p className="text-smoke text-sm mt-4 flex items-center justify-center gap-1.5" data-id="element-88">
            ⚡ Instant digital delivery — straight to your inbox
          </p>
        </motion.div>
      </div>
    </section>;
}